import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { contentAPI } from "../services/api";
import { useLanguage } from "../hooks/useLanguage";

const MDKSection = () => {
  const currentLanguage = useLanguage();
  const [sectionData, setSectionData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    const fetchSectionData = async () => {
      try {
        const sections = await contentAPI.getSections("home").catch(() => []);
        const mdkSection = sections.find((s) => s.section_type === "mdk");
        if (mdkSection?.content_data) {
          setSectionData(mdkSection.content_data);
        }
      } catch (error) {
        console.error("Error fetching MDK section:", error);
      }
    };
    fetchSectionData();
  }, []);

  useEffect(() => {
    if (!previewOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") setPreviewOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [previewOpen]);

  const getStringValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value !== null) {
      return value[currentLanguage] || value["de"] || value[Object.keys(value)[0]] || fallback;
    }
    return String(value) || fallback;
  };

  const commitmentText = sectionData
    ? getStringValue(sectionData.quality_commitment_text, "")
    : "";
  const qualityBild = sectionData?.quality_bild || null;

  if (!commitmentText && !qualityBild) return null;

  return (
    <section id="mdk" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-[var(--vortex-bg)]">
      <div className="max-w-4xl mx-auto w-full min-w-0 flex flex-col items-center">
        {commitmentText && (
          <div
            className="w-full rounded-xl px-4 sm:px-6 py-5 sm:py-6 mb-6 sm:mb-8 text-center"
            style={{ backgroundColor: "rgb(248, 245, 238)" }}
          >
            <p className="text-base sm:text-lg text-[var(--vortex-text-primary)] leading-relaxed max-w-3xl mx-auto">
              {commitmentText}
            </p>
          </div>
        )}

        {qualityBild && (
          <>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="relative w-full max-w-2xl mx-auto rounded-xl overflow-hidden border-2 border-[var(--vortex-border)] sm:border-[#007bff] shadow-lg block text-left cursor-pointer hover:border-[var(--vortex-accent-primary)] hover:shadow-xl transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vortex-accent-primary)] focus-visible:ring-offset-2 bg-white"
              aria-label="Qualitätsbericht vergrößern"
            >
              <img
                src={qualityBild}
                alt="Qualitätsbericht des ambulanten Pflegedienstes"
                className="w-full h-auto object-contain pointer-events-none"
              />
            </button>

            {previewOpen && (
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Bildvorschau"
                className="fixed inset-0 z-[100] flex items-center justify-center p-4 pt-14 sm:pt-4 bg-black/80"
                onClick={() => setPreviewOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setPreviewOpen(false)}
                  className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 hover:bg-white text-[var(--vortex-text-primary)] shadow-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/80"
                  aria-label="Vorschau schließen"
                >
                  <X className="w-6 h-6 sm:w-8 sm:h-8" />
                </button>
                <div
                  className="max-w-[95vw] max-h-[85vh] sm:max-h-[90vh] flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img
                    src={qualityBild}
                    alt="Qualitätsbericht (Vorschau)"
                    className="max-w-full max-h-[85vh] sm:max-h-[90vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default MDKSection;
